import React, { useCallback, useState } from 'react';

import { Button, Input, Modal, showToast } from '@attentive-platform/stem-ui';

import { ModalActions, ModalBody, ModalSubtitle, ModalTitle } from '../../styled';
import { apiErrorMessage } from '../../Utils/apiErrors';
import { isValidEmail } from '../../Utils/HelperFunctions';
import { CompanyPicker } from '../SharedComponent';
import { createSniperEstimator, updateSniperEstimator } from './api';
import { CompaniesLabel } from './styles';

const AccountModal = ({ open, mode, estimator, onClose, onSaved }) => {
  const isCreate = mode === 'create';
  const [email, setEmail] = useState('');
  const [companies, setCompanies] = useState(() => estimator?.companies || []);
  const [submitting, setSubmitting] = useState(false);

  const trimmedEmail = email.trim();
  const subtitle = estimator?.email || estimator?.raw?.email || '';
  const handleSearchError = useCallback(message => showToast(message, 'error'), []);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleEmailChange = event => setEmail(event.target.value);

  const handleSubmit = async () => {
    if (submitting) return;
    if (isCreate && !trimmedEmail) {
      showToast('Please enter an email address', 'error');
      return;
    }
    if (isCreate && !isValidEmail(trimmedEmail)) {
      showToast('Please enter a valid email address', 'error');
      return;
    }

    setSubmitting(true);
    try {
      if (isCreate) {
        const created = await createSniperEstimator({
          email: trimmedEmail,
          companyIds: companies.map(company => company.id)
        });
        if (created.error) {
          showToast(apiErrorMessage(created.error, 'Could not create account'), 'error');
          return;
        }
        showToast('Account created', 'success');
        onSaved(created.data);
        return;
      } else {
        const updated = await updateSniperEstimator({
          userId: estimator.id,
          companyIds: companies.map(company => company.id)
        });
        if (updated.error) {
          showToast(apiErrorMessage(updated.error, 'Could not update companies'), 'error');
          return;
        }
        showToast('Companies updated', 'success');
        onSaved(updated.data);
        return;
      }
    } catch {
      showToast(isCreate ? 'Could not create account' : 'Could not update companies', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={open}
      onClose={handleClose}
      heading={<ModalTitle>{isCreate ? 'Create account' : 'Assign companies'}</ModalTitle>}
      content={
        <ModalBody>
          {isCreate ? (
            <div>
              <label className='mb-2 block text-sm font-medium text-gray-900'>Email</label>
              <Input
                value={email}
                className='w-full'
                name='estimator-account-email'
                autoComplete='off'
                placeholder='name@attentive.ai'
                inputProps={{ autoComplete: 'off' }}
                onChange={handleEmailChange}
              />
            </div>
          ) : (
            <ModalSubtitle>{subtitle}</ModalSubtitle>
          )}

          <div>
            <CompaniesLabel>Companies</CompaniesLabel>
            <CompanyPicker selected={companies} onChange={setCompanies} onError={handleSearchError} />
          </div>

          <ModalActions>
            <Button size='medium' variant='text' color='primary' disabled={submitting} onClick={handleClose}>
              Cancel
            </Button>
            <Button color='primary' size='medium' loading={submitting} onClick={handleSubmit}>
              {isCreate ? 'Create account' : 'Save'}
            </Button>
          </ModalActions>
        </ModalBody>
      }
    />
  );
};

export default AccountModal;
